import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, FormControl, FormLabel, MenuItem, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from '../../../../MeetingHubContext';
import { useMeetingStaffContext } from '../../../MeetingStaffContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../../MotionVotingControls';
import { Motion } from 'store/types/Meeting';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  useSafeAttendeeDisplayValue,
  useSafeSelectIdValue,
} from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

const getMotionOptions = (motions: Motion[]) =>
  motions.map((m) => (
    <MenuItem key={m.id} value={m.id}>
      {m.label || m.title}
    </MenuItem>
  ));

const idLabel = 'motion';
const defaultStatement = 'Negative is addressed by the technical change(s).';

const TechnicalChangeMotion: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { motions, currentDocument, documentExists, currentReject, setCurrentMotion, saveMotion, deleteMotion } =
    useMeetingStaffContext();
  const [selectedMotionId, setSelectedMotionId] = useState('');
  const [draftMotionId, setDraftMotionId] = useState(() => `${idLabel}-${Date.now()}`);
  const [fromText, setFromText] = useState('');
  const [toText, setToText] = useState('');
  const [justification, setJustification] = useState('');
  const [statement, setStatement] = useState(defaultStatement);
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const documentMotions = useMemo(
    () => motions?.filter((m) => m.documentId === currentDocument?.id),
    [currentDocument?.id, motions]
  );

  const motionOptions = useMemo(() => getMotionOptions(documentMotions), [documentMotions]);
  const safeSelectedMotionId = useSafeSelectIdValue(selectedMotionId, documentMotions);

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, attendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, attendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: selectedMotionId || draftMotionId,
      title: 'Technical Change',
      label: currentReject
        ? `Reject Name: ${currentReject.rejectedBy ?? ''} - Negative #: ${currentReject.number ?? ''} `
        : 'Technical Change',
      documentId: currentDocument?.id,
      fromText,
      toText,
      justification,
      statement,
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [
    currentDocument?.id,
    currentReject,
    discussion,
    draftMotionId,
    fromText,
    justification,
    safeBy,
    safeSecond,
    selectedMotionId,
    statement,
    toText,
  ]);

  const handleMotionChange = useCallback((event) => {
    const motionId = event.target.value;

    setSelectedMotionId(motionId);
    if (!motionId) {
      setDraftMotionId(`${idLabel}-${Date.now()}`);
    }
  }, []);

  const makeSaveMotionHandler = useCallback(
    (notify?: boolean) => async (): Promise<Motion | null> => {
      const saved = await saveMotion?.();

      if (saved) {
        if (notify) {
          enqueueSnackbar(`Motion saved`, { variant: 'success' });
        }
        setSelectedMotionId(saved.id);
        setDraftMotionId(`${idLabel}-${Date.now()}`);
        return saved;
      }

      return null;
    },
    [enqueueSnackbar, saveMotion]
  );

  const handleSaveMotion = useMemo(() => makeSaveMotionHandler(true), [makeSaveMotionHandler]);
  const handleSaveMotionNoNotify = useMemo(() => makeSaveMotionHandler(false), [makeSaveMotionHandler]);

  const handleDeleteMotion = useCallback(() => {
    if (!selectedMotionId) {
      enqueueSnackbar(`No motion selected to delete`, { variant: 'warning' });
      return;
    }

    deleteMotion?.(selectedMotionId);
  }, [selectedMotionId, deleteMotion, enqueueSnackbar]);

  const handleClearMotion = useCallback(() => {
    setSelectedMotionId('');
    setDraftMotionId(`${idLabel}-${Date.now()}`);
    setFromText('');
    setToText('');
    setJustification('');
    setStatement(defaultStatement);
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  useEffect(() => {
    if (!documentMotions?.length) return;

    const motion = documentMotions.find((m) => m.id === selectedMotionId);

    setFromText(motion?.fromText || '');
    setToText(motion?.toText || '');
    setJustification(motion?.justification || '');
    setStatement(motion?.statement || '');
    setBy(motion?.by || '');
    setSecond(motion?.second || '');
    setDiscussion(motion?.discussion || '');
  }, [documentMotions, selectedMotionId]);

  useEffect(() => {
    setCurrentMotion?.(currentMotion);
  }, [currentMotion, setCurrentMotion]);

  return (
    <GridContainer>
      <GridItem md={6}>
        <TextField label={'Load Technical Change'} select value={safeSelectedMotionId} onChange={handleMotionChange}>
          <MenuItem value={''}>-- New Technical Change --</MenuItem>
          {motionOptions}
        </TextField>
      </GridItem>
      <GridItem md={6} className={commonStyles.buttonGroup}>
        <Button variant="contained" color="primary" onClick={handleSaveMotion}>
          Save
        </Button>
        <Button variant="contained" color="error" onClick={handleDeleteMotion}>
          Delete
        </Button>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>From *</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="technical-change-from-text-editor"
            value={fromText}
            onEditorChange={(newValue: string) => setFromText(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>To *</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="technical-change-to-text-editor"
            value={toText}
            onEditorChange={(newValue: string) => setToText(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Justification</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="technical-change-justification-editor"
            value={justification}
            onEditorChange={(newValue: string) => setJustification(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <TextField
          label={'Motion'}
          value={statement}
          required
          onChange={(event) => {
            setStatement(event.target.value);
          }}
        ></TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'By'}
          select
          required
          value={safeBy}
          onChange={(event) => {
            setBy(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {byAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'Second'}
          select
          required
          value={safeSecond}
          onChange={(event) => {
            setSecond(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {secondAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Discussion</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id="technical-change-discussion-editor"
            value={discussion}
            onEditorChange={(newValue: string) => setDiscussion(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          requiredFields={['fromText', 'toText', 'statement', 'by', 'second']}
          showVotingRuleSelector={false}
          validate={() => {
            if (!documentExists) {
              enqueueSnackbar(`Document must be saved or displayed before adding a technical change`, {
                variant: 'warning',
              });
              return false;
            }

            return true;
          }}
          saveMotion={handleSaveMotionNoNotify}
          onClearMotion={handleClearMotion}
        />
      </GridItem>
    </GridContainer>
  );
};

export default TechnicalChangeMotion;
